import { PRODUCTION_MODEL } from "../../../../services/claude";
import { REQUEST_CANCELED_ERROR } from "../../constants/errors";
import { EMPTY_LENGTH } from "../../constants/numeric";
import { generateSchemaFromInitialContext } from "../../rag-chunking/chunkPreview";
import { processWithChunks } from "../../rag-chunking/chunkProcessing";
import { createChunks } from "../../rag-chunking/chunkSplitting";
import { ChatActions, ChatGetState, ChatMessage, ChatSetState, ChatState, DataChunk } from "../chatTypes";
import { buildQueryWithModeInjection } from "./chatMessages";

type ChunkingActions = Pick<
  ChatActions,
  | "setCreatingChunks"
  | "startChunkingProcess"
  | "enableChunking"
  | "clearTokenLimitMessages"
  | "showChunkConfirmation"
  | "sendChunkedMessage"
>;

const isCanceledError = (error: unknown): boolean =>
  error instanceof Error && error.message === REQUEST_CANCELED_ERROR;

const throwIfAborted = (signal: AbortSignal): void => {
  if (signal.aborted) {
    throw new Error(REQUEST_CANCELED_ERROR);
  }
};

const buildUserMessage = (content: string, state: ChatState, temperature: number): ChatMessage => ({
  id: crypto.randomUUID(),
  content,
  role: "user",
  timestamp: new Date(),
  temperature,
  includedArtifactTypes: [...state.includedArtifactTypes],
  answerMode: state.answerMode
});

const buildChunkSystemMessage = (state: ChatState): ChatMessage => ({
  id: crypto.randomUUID(),
  content: "Creating chunks...",
  role: "system",
  timestamp: new Date(),
  contextData: state.contextData,
  isStreaming: true
});

const addErrorMessageFor = (get: ChatGetState, error: unknown): void => {
  const errorMessage = error instanceof Error ? error.message : "An error occurred processing your request";
  get().addMessage({
    id: crypto.randomUUID(),
    content: `Error: ${errorMessage}`,
    role: "error",
    timestamp: new Date()
  });
};

interface StartChunkingParams {
  content: string;
  temperature: number;
}

const executeStartChunking = async (set: ChatSetState, get: ChatGetState, params: StartChunkingParams): Promise<void> => {
  const { content, temperature } = params;
  const state = get();

  if (state.editingMessage) {
    get().truncateMessagesAfter(state.editingMessage);
    set({ editingMessage: null });
  }

  const { query: injectedQuery, injected } = buildQueryWithModeInjection(
    content,
    state.answerMode,
    state.lastInjectedAnswerMode
  );

  if (injected || state.answerMode === "none") {
    set({ lastInjectedAnswerMode: state.answerMode });
  }

  get().addMessage(buildUserMessage(content, state, temperature));
  get().addToHistory({ role: "user", content: injectedQuery });

  const abortController = new AbortController();
  get().setAbortController(abortController);

  try {
    await generateSchemaFromInitialContext({
      content: injectedQuery,
      contextData: state.contextData ?? [],
      state,
      temperature,
      get,
      abortSignal: abortController.signal
    });

    await get().showChunkConfirmation({ content: injectedQuery, temperature });
  } catch (error) {
    if (isCanceledError(error)) {
      return;
    }
    const errorMessage = error instanceof Error ? error.message : "An error occurred during schema generation";
    get().addMessage({
      id: crypto.randomUUID(),
      content: `Error: ${errorMessage}`,
      role: "error",
      timestamp: new Date()
    });
  } finally {
    get().setAbortController(null);
  }
};

interface ConfirmChunksParams {
  content: string;
  temperature: number;
  systemMessage: ChatMessage;
  abortController: AbortController;
}

const createChunksForConfirmation = async (
  get: ChatGetState,
  set: ChatSetState,
  params: ConfirmChunksParams
): Promise<DataChunk[]> => {
  const { content, abortController } = params;
  const state = get();

  throwIfAborted(abortController.signal);

  const chunks = await createChunks({
    contextData: state.contextData ?? [],
    totalCalculatedTokens: state.totalCalculatedTokens,
    singleContextTokenLength: state.singleContextTokenLength,
    model: state.model ?? PRODUCTION_MODEL.id,
    query: content,
    abortSignal: abortController.signal,
    set
  });

  throwIfAborted(abortController.signal);

  if (!chunks || chunks.length === EMPTY_LENGTH) {
    throw new Error("Failed to create data chunks");
  }

  return chunks;
};

interface ApplyConfirmedChunksParams {
  confirmParams: ConfirmChunksParams;
  chunks: DataChunk[];
}

const applyConfirmedChunks = (get: ChatGetState, set: ChatSetState, params: ApplyConfirmedChunksParams): void => {
  const { confirmParams, chunks } = params;
  const { content, temperature, systemMessage } = confirmParams;
  get().updateMessage(systemMessage.id, {
    content: `Ready to process your query in ${chunks.length} chunks`,
    chunks,
    isStreaming: false
  });
  set({
    pendingQuery: {
      content,
      temperature,
      chunks,
      systemMessageId: systemMessage.id
    },
    continueChunkedQuery: async (): Promise<void> => {
      await get().sendChunkedMessage();
    }
  });
};

interface ConfirmationErrorParams {
  systemMessage: ChatMessage;
  error: unknown;
}

const handleChunkConfirmationError = (get: ChatGetState, params: ConfirmationErrorParams): void => {
  const { systemMessage, error } = params;
  if (isCanceledError(error)) {
    get().updateMessage(systemMessage.id, {
      content: "Chunk creation was stopped",
      isStreaming: false
    });
    return;
  }

  const message = error instanceof Error ? error.message : "Unknown error";
  get().updateMessage(systemMessage.id, { content: `Error creating chunks: ${message}` });
  throw error;
};

const finalizeChunkConfirmation = (get: ChatGetState, set: ChatSetState): void => {
  get().setAbortController(null);
  set({ isCreatingChunks: false, inputValue: "" });
};

const executeShowChunkConfirmation = async (
  set: ChatSetState,
  get: ChatGetState,
  params: { content: string; temperature: number }
): Promise<void> => {
  const systemMessage = buildChunkSystemMessage(get());
  get().addMessage(systemMessage);

  const abortController = new AbortController();
  get().setAbortController(abortController);
  set({ isCreatingChunks: true });

  const confirmParams: ConfirmChunksParams = { ...params, systemMessage, abortController };

  try {
    const chunks = await createChunksForConfirmation(get, set, confirmParams);
    applyConfirmedChunks(get, set, { confirmParams, chunks });
  } catch (error) {
    handleChunkConfirmationError(get, { systemMessage, error });
  } finally {
    finalizeChunkConfirmation(get, set);
  }
};

const markProcessingStart = (set: ChatSetState, systemMessageId: string): void => {
  set((state) => ({
    messages: state.messages.map((msg) =>
      msg.id === systemMessageId
        ? { ...msg, content: "Processing your query in chunks..." }
        : msg
    )
  }));
};

const executeSendChunkedMessage = async (set: ChatSetState, get: ChatGetState): Promise<void> => {
  const { pendingQuery } = get();
  if (!pendingQuery) {
    return;
  }
  const state = get();

  const abortController = new AbortController();
  get().setAbortController(abortController);
  get().setLoading(true);
  markProcessingStart(set, pendingQuery.systemMessageId);

  try {
    await processWithChunks({
      content: pendingQuery.content,
      state,
      temperature: pendingQuery.temperature,
      get,
      abortSignal: abortController.signal,
      chunks: pendingQuery.chunks
    });
    set({ pendingQuery: null, continueChunkedQuery: null });
  } catch (error) {
    if (isCanceledError(error)) {
      return;
    }
    addErrorMessageFor(get, error);
  } finally {
    get().setAbortController(null);
    get().handleComplete();
    get().setLoading(false);
  }
};

export const createChunkingActions = (set: ChatSetState, get: ChatGetState): ChunkingActions => ({
  setCreatingChunks: (creating: boolean): void => {
    set({ isCreatingChunks: creating });
  },

  startChunkingProcess: async (params: { content: string; temperature: number }): Promise<void> => {
    await executeStartChunking(set, get, params);
  },

  enableChunking: async (): Promise<void> => {
    const { isChunkingEnabled, contextData, addMessage, clearTokenLimitMessages } = get();

    if (isChunkingEnabled) {
      return;
    }

    set({ isChunkingEnabled: true });
    clearTokenLimitMessages();

    const contextDataLength = contextData?.length ?? EMPTY_LENGTH;
    const summary = `Chunking enabled! Context contains ${contextDataLength} artifacts.`;
    const details = "Token limits removed - data will be chunked automatically if needed when you send a message.";

    addMessage({
      id: crypto.randomUUID(),
      content: `${summary} ${details}`,
      role: "system",
      timestamp: new Date(),
      exceedsTokenLimit: false
    });
  },

  clearTokenLimitMessages: (): void => {
    set((state) => ({
      messages: state.messages.filter((msg) => !msg.exceedsTokenLimit)
    }));
  },

  showChunkConfirmation: async (params: { content: string; temperature: number }): Promise<void> => {
    await executeShowChunkConfirmation(set, get, params);
  },

  sendChunkedMessage: async (): Promise<void> => {
    await executeSendChunkedMessage(set, get);
  }
});
